import { useMemo } from "react";

import { clamp } from "lodash";

import { type SalesPageMetric } from "@north-beam/nb-client-side";

import {
  FIXED_NAME_COLUMN,
  FIXED_STATUS_COLUMN,
} from "../table/components/constants";

import { type ColumnConfig, useTableState } from "./use-table-state";

export interface ColumnDef extends ColumnConfig {
  title: string;
  name: string;
  metric: SalesPageMetric;
  getCellValue?: (row: any, columnName: string) => any;
  comparison?: any;
  options?: Record<string, any>;
  loading?: boolean;
  compareDataLoading?: boolean;
}

export const getDefaultCellWidth = (title: string) =>
  title === "Spend" ? 180 : clamp(140, title.length * 9, 200);

const createColumnDefinition = (metrics: SalesPageMetric[]) =>
  metrics
    .filter((v) => !v.hideFromTableDisplay)
    .map((metric) => {
      const columnDef: ColumnDef = {
        id: metric.id,
        // DX uses "name" as the id field for the column
        name: metric.id,
        title: metric.name,
        getCellValue: (row: any) => row?.export?.[metric.id] ?? row?.value,
        width: getDefaultCellWidth(metric.name),
        showMiniGraph: false,
        metric,
      };

      if (!metric.hideComparisonFromTableCell) {
        columnDef.comparison = {
          isPositiveChangeGood: metric.isPositiveChangeGood,
        };
      }

      return columnDef;
    });

export const useTableColumns = (
  metrics: SalesPageMetric[],
  options: { hideStatusColumn?: boolean } = {},
) => {
  const { columnConfigs } = useTableState();

  const columns = useMemo(() => {
    const initialColumns = [FIXED_NAME_COLUMN] as ColumnDef[];

    if (!options.hideStatusColumn) {
      initialColumns.push(FIXED_STATUS_COLUMN as ColumnDef);
    }

    const columnDefinitions = createColumnDefinition(metrics);
    const columnDefinitionsWithConfigs = columnConfigs.reduce<ColumnDef[]>(
      (acc, config) => {
        const columnDef = columnDefinitions.find(
          (d) => d.id === config.id || d.name === config.id,
        );
        if (columnDef) {
          // DX uses "name" as the id field for the column
          return [...acc, { ...columnDef, ...config, name: columnDef.id }];
        }
        return acc;
      },
      initialColumns,
    );

    return columnDefinitionsWithConfigs;
  }, [columnConfigs, metrics, options.hideStatusColumn]);

  return { columns };
};
